import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'

import type { ColumnDef } from '@tanstack/react-table'
import type { ConvexDirectReport } from '../services/direct-reports.convex'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { DataTable } from '@/components/common/data-table'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'

interface DirectReportsTableProps {
  directReports: ConvexDirectReport[]
  currentUserId: string
  onCreateAssessment: (
    directReportUserId: string,
    directReportName: string,
  ) => void
  onTakeSurvey: (assessmentId: string, responseId: string) => void
  isCreatingAssessment?: boolean
  isLoading?: boolean
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

interface ActionsCellProps {
  directReport: ConvexDirectReport
  currentUserId: string
  onCreateAssessment: (
    directReportUserId: string,
    directReportName: string,
  ) => void
  onTakeSurvey: (assessmentId: string, responseId: string) => void
  isCreatingAssessment?: boolean
}

function ActionsCell({
  directReport,
  onCreateAssessment,
  onTakeSurvey,
  isCreatingAssessment = false,
}: ActionsCellProps) {
  const { showConfirmDialog } = useConfirmDialog()
  const { pendingAssessment } = directReport

  const handleCreateAssessment = () => {
    showConfirmDialog({
      title: 'Create Assessment',
      description: `Are you sure you want to create a new assessment for ${directReport.name ?? directReport.email}?`,
      confirmText: 'Create Assessment',
      cancelText: 'Cancel',
      onConfirm: () => {
        onCreateAssessment(directReport.userId, directReport.name ?? '')
      },
    })
  }

  const handleTakeSurvey = () => {
    if (!pendingAssessment) return
    onTakeSurvey(pendingAssessment.assessmentId, pendingAssessment.responseId)
  }

  return (
    <div className="flex items-center gap-2">
      <Button asChild variant="outline" size="sm">
        <Link
          to="/direct-reports/$directReportId/career-path"
          params={{ directReportId: directReport.userId }}
          search={{ name: directReport.name ?? undefined }}
        >
          View Profile
        </Link>
      </Button>
      {pendingAssessment ? (
        <Button variant="default" size="sm" onClick={handleTakeSurvey}>
          Take Survey
        </Button>
      ) : (
        <Button
          variant="default"
          size="sm"
          onClick={handleCreateAssessment}
          disabled={isCreatingAssessment}
        >
          {isCreatingAssessment ? 'Creating...' : 'Create Assessment'}
        </Button>
      )}
    </div>
  )
}

export function DirectReportsTable({
  directReports,
  currentUserId,
  onCreateAssessment,
  onTakeSurvey,
  isCreatingAssessment,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: DirectReportsTableProps) {
  const columns = useMemo<Array<ColumnDef<ConvexDirectReport>>>(
    () => [
      {
        accessorKey: 'name',
        header: 'User',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <AvatarWithInitials
              name={row.original.name ?? row.original.email ?? '?'}
              src={row.original.image ?? undefined}
              className="h-10 w-10"
            />
            <div>
              <div className="font-medium">{row.original.name}</div>
              <div className="text-sm text-muted-foreground">
                {row.original.email}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'position',
        header: 'Position',
        cell: ({ row }) => {
          const { position } = row.original
          return position ? (
            <div>
              <div className="font-medium">{position.title}</div>
              <div className="text-sm text-muted-foreground">
                {position.role} - {position.level}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">No position assigned</span>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <ActionsCell
            directReport={row.original}
            currentUserId={currentUserId}
            onCreateAssessment={onCreateAssessment}
            onTakeSurvey={onTakeSurvey}
            isCreatingAssessment={isCreatingAssessment}
          />
        ),
      },
    ],
    [currentUserId, onCreateAssessment, onTakeSurvey, isCreatingAssessment],
  )

  if (isLoading) {
    return (
      <div className="bg-muted/30 rounded-lg p-6">
        <div className="relative w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50 border-b transition-colors">
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  User
                </TableHead>
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  Position
                </TableHead>
                <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow
                  key={i}
                  className="hover:bg-muted/50 border-b transition-colors"
                >
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-muted animate-pulse rounded-full" />
                      <div className="space-y-2">
                        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                        <div className="h-3 w-48 bg-muted animate-pulse rounded" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                      <div className="h-3 w-32 bg-muted animate-pulse rounded" />
                    </div>
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-24 bg-muted animate-pulse rounded" />
                      <div className="h-8 w-32 bg-muted animate-pulse rounded" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={directReports}
        emptyMessage="No direct reports found."
      />
      {onLoadMore && hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
